import _ from 'lodash';

import { ActionStepType } from 'constants/aiChat';

export const actionPlanItemKind = {
  STEP: 'step',
  GROUP: 'group',
};

export const ACTION_PLAN_LABELS = {
  CANCEL: 'Cancel',
  HEADER: 'Plan',
  EXPAND: 'Show plan',
  COLLAPSE: 'Hide plan',
  PROGRESS: ({ done, total }) => `${done} of ${total} done`,
  PLAN_FAILED: 'Plan failed',
  PLAN_CANCELLED: 'Plan cancelled',
  LOADING: 'Loading…',
  PLANNING: 'Planning…',
};

const USE_SKILL_PREFIX = 'use_skill_';
const INVOKE_AGENT_PREFIX = 'invoke_agent_';

// Mirror of BE's ToolFriendlyNameMapper.GetSubStepType. Keeping the mapping
// FE-side lets the planner row pick a meaningful label/icon (e.g. "Reading
// Data") instead of the generic "Executing Command" for every TOOL_CALL step.
const TOOL_NAME_PREFIX_TYPE_RULES = [
  { prefixes: [USE_SKILL_PREFIX], type: ActionStepType.SKILL_USE },
  { prefixes: [INVOKE_AGENT_PREFIX], type: ActionStepType.AGENT_DELEGATION },
  { prefixes: ['get_', 'read_', 'list_', 'query_', 'search_'], type: ActionStepType.DATA_READING },
  {
    prefixes: ['update_', 'edit_', 'create_', 'delete_', 'set_', 'write_'],
    type: ActionStepType.MODIFYING_RECORD,
  },
  { prefixes: ['navigate_', 'open_'], type: ActionStepType.EXECUTING_COMMAND },
];

export const resolveActionStepTypeFromToolName = toolName => {
  if (!_.isString(toolName) || toolName.length === 0) {
    return null;
  }

  const normalized = toolName.toLowerCase();

  if (normalized.includes('guideline')) {
    return ActionStepType.GUIDELINE_LOOKUP;
  }

  const matchedRule = TOOL_NAME_PREFIX_TYPE_RULES.find(rule =>
    rule.prefixes.some(prefix => normalized.startsWith(prefix)),
  );

  return matchedRule?.type ?? null;
};

// Mirror of BE's ToolFriendlyNameMapper.FriendlyNames. The persisted execution
// plan blob only carries `tool` + `description` per step, so on rehydration
// the FE has no `label` to show as a short title. Without this mapping
// refreshed plan cards fall back to the planner's long sentence and lose the
// title-vs-Why duality the streaming view has. Keeping the table FE-side
// avoids a round-trip + makes post-refresh and live-streaming renders match
// exactly. Labels are subject-only and Title Case — the action-step chip
// carries the verb (e.g. "READING"), so a row reads as
// [icon] [READING] Borrower Info. Acronyms like REO stay uppercase.
const TOOL_FRIENDLY_NAME_MAP = {
  get_borrowers: 'Borrower Info',
  get_borrower: 'Borrower Details',
  get_contacts: 'Contact Info',
  get_loan_application: 'Loan Application',
  get_loan_roles: 'Loan Roles',
  get_properties: 'Property Info',
  get_real_estate_owned: 'REO Info',
  get_assets: 'Asset Info',
  get_liabilities: 'Liability Info',
  get_incomes: 'Income Info',
  get_expenses: 'Expense Info',
  get_declarations: 'Declarations',
  get_conditions: 'Loan Conditions',
  get_loan_tasks: 'Loan Tasks',
  get_documents: 'Loan Documents',
  get_closing_info: 'Closing Info',
  get_funding_info: 'Funding Info',
  get_secondary_financing: 'Secondary Financing',
  query_guidelines: 'Underwriting Guidelines',
  get_guideline_section: 'Guideline Section',
  get_guideline_toc: 'Guideline Table of Contents',
  create_execution_plan: 'Execution Plan',
  update_execution_plan: 'Execution Plan',
  get_navigation_catalog: 'Navigation Options',
  open_loan_section: 'Loan Section',
  add_adhoc_conditions: 'Add custom conditions',
  add_conditions_from_library: 'Add conditions from library',
  commit_loan_changes: 'Commit loan changes',
};

const formatToolNameAsLabel = toolName => {
  const words = toolName.split('_').filter(word => word.length > 0);

  if (words.length === 0) {
    return toolName;
  }

  return words.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
};

export const resolveFriendlyToolLabel = toolName => {
  if (!_.isString(toolName) || toolName.length === 0) {
    return null;
  }

  if (TOOL_FRIENDLY_NAME_MAP[toolName]) {
    return TOOL_FRIENDLY_NAME_MAP[toolName];
  }

  const normalized = toolName.toLowerCase();

  if (normalized.startsWith(USE_SKILL_PREFIX)) {
    return formatToolNameAsLabel(toolName.slice(USE_SKILL_PREFIX.length));
  }

  if (normalized.startsWith(INVOKE_AGENT_PREFIX)) {
    return formatToolNameAsLabel(toolName.slice(INVOKE_AGENT_PREFIX.length));
  }

  return formatToolNameAsLabel(toolName);
};
